<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useQuery, useQueryClient } from "@tanstack/vue-query";

import {
  approveWeeklyReport,
  draftWeeklyReportNarrative,
  fetchWeeklyReportPdf,
  getWeeklyReport,
  getWeeklyReportHistory,
  pushEvents,
  reopenWeeklyReport,
  reviewWeeklyReport,
  setWeeklyReportRag,
  submitWeeklyReport,
  suggestWeeklyReportRag,
  type NewEvent,
  type RagSuggestion,
  type WeeklyReviewFinding,
} from "@/api/client";
import PageLayout from "@/components/PageLayout.vue";
import { usePermissions } from "@/lib/permissions";

const route = useRoute();
const id = computed(() => route.params.id as string);
const queryClient = useQueryClient();
// Approver-role gate (matches the server + mobile) for approve / reopen.
const { canApprove } = usePermissions();

const { data, isPending, isError, error } = useQuery({
  queryKey: ["weekly-report", id],
  queryFn: () => getWeeklyReport(id.value),
});
const { data: history } = useQuery({
  queryKey: ["weekly-report-history", id],
  queryFn: () => getWeeklyReportHistory(id.value),
});

const RAG_DIMS = [
  { key: "scope", label: "Scope" },
  { key: "budget", label: "Budget" },
  { key: "timeline", label: "Timeline" },
  { key: "quality", label: "Quality" },
] as const;
const RAG_OPTS = [
  { value: "green", label: "G", cls: "bg-semantic-success text-white" },
  { value: "amber", label: "A", cls: "bg-semantic-warning text-white" },
  { value: "red", label: "R", cls: "bg-semantic-danger text-white" },
] as const;

const busy = ref(false);
async function act(fn: () => Promise<void>): Promise<void> {
  busy.value = true;
  saveError.value = null;
  try {
    await fn();
    await queryClient.invalidateQueries({ queryKey: ["weekly-report", id] });
    await queryClient.invalidateQueries({ queryKey: ["weekly-report-history", id] });
  } catch (e) {
    saveError.value = e instanceof Error ? e.message : String(e);
  } finally {
    busy.value = false;
  }
}
function setRag(dim: string, v: "red" | "amber" | "green"): void {
  void act(() => setWeeklyReportRag(id.value, dim, v));
}

// --- AI-suggested RAG (Phase 3) ---
// Suggests each dimension from the week's data with a rationale; advisory — the
// author applies a suggestion (or sets their own) via the existing buttons.
const ragBusy = ref(false);
const ragSuggestions = ref<Record<string, RagSuggestion> | null>(null);
async function suggestRag(): Promise<void> {
  ragBusy.value = true;
  saveError.value = null;
  try {
    const r = await suggestWeeklyReportRag(id.value);
    ragSuggestions.value = r.suggestions;
  } catch (e) {
    saveError.value = e instanceof Error ? e.message : String(e);
  } finally {
    ragBusy.value = false;
  }
}
const RAG_WORD: Record<string, string> = {
  green: "Green",
  amber: "Amber",
  red: "Red",
};
// --- Pre-submit review (Phase 2) ---
// First click runs the review; if it's clean, the report submits; if not, the
// findings show and the button becomes "Submit anyway" (advisory, never blocks).
const reviewBusy = ref(false);
const reviewMsg = ref<string | null>(null);
const reviewFindings = ref<WeeklyReviewFinding[] | null>(null);
async function submit(): Promise<void> {
  if (reviewFindings.value && reviewFindings.value.length) {
    reviewFindings.value = null;
    reviewMsg.value = null;
    await act(() => submitWeeklyReport(id.value));
    return;
  }
  reviewBusy.value = true;
  reviewMsg.value = null;
  try {
    const r = await reviewWeeklyReport(id.value);
    if (r.ok) {
      await act(() => submitWeeklyReport(id.value));
    } else {
      reviewFindings.value = r.findings;
      reviewMsg.value = `${r.findings.length} thing(s) to check before submitting.`;
    }
  } catch (e) {
    saveError.value = e instanceof Error ? e.message : String(e);
  } finally {
    reviewBusy.value = false;
  }
}
function approve(): void {
  void act(() => approveWeeklyReport(id.value));
}
function reopen(): void {
  void act(() => reopenWeeklyReport(id.value));
}

const fmt = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString("en-GB", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  }) : "—";

const SECTIONS = [
  { key: "summary", label: "Progress summary" },
  { key: "roadblocks", label: "Roadblocks, delays & risks" },
  { key: "look_ahead", label: "Look-ahead — next week" },
  { key: "resources", label: "Resources" },
] as const;

// Editable narrative, seeded from the report once it loads.
const draft = ref<Record<string, string>>({});
watch(
  data,
  (d) => {
    if (d) {
      draft.value = Object.fromEntries(
        SECTIONS.map((s) => [s.key, d.sections[s.key] ?? ""]),
      );
    }
  },
  { immediate: true },
);

const saving = ref(false);
const saveError = ref<string | null>(null);
const pdfBusy = ref(false);

// --- AI draft of the narrative (Phase 1) ---
// Fills the empty sections from this week's rolled-up data; never clobbers what
// the author has already written. The author edits, then Saves as usual.
const drafting = ref(false);
const draftMsg = ref<string | null>(null);
async function draftNarrative(): Promise<void> {
  drafting.value = true;
  draftMsg.value = null;
  try {
    const r = await draftWeeklyReportNarrative(id.value);
    let filled = 0;
    for (const s of SECTIONS) {
      const v = (r as Record<string, string | null>)[s.key];
      if (v && v.trim() && !draft.value[s.key]?.trim()) {
        draft.value[s.key] = v.trim();
        filled++;
      }
    }
    draftMsg.value = filled
      ? `Drafted ${filled} empty section(s) — review and Save narrative.`
      : "All sections already have content — clear one to redraft it.";
  } catch (e) {
    draftMsg.value = e instanceof Error ? e.message : String(e);
  } finally {
    drafting.value = false;
  }
}

async function downloadPdf(): Promise<void> {
  pdfBusy.value = true;
  try {
    const blob = await fetchWeeklyReportPdf(id.value);
    window.open(URL.createObjectURL(blob), "_blank");
  } catch (e) {
    saveError.value = e instanceof Error ? e.message : String(e);
  } finally {
    pdfBusy.value = false;
  }
}

async function saveNarrative(): Promise<void> {
  if (!data.value) return;
  const changed: NewEvent[] = [];
  for (const s of SECTIONS) {
    if (draft.value[s.key] !== (data.value.sections[s.key] ?? "")) {
      changed.push({
        aggregate_type: "weekly_report",
        aggregate_id: id.value,
        event_type: "WeeklyReportSectionUpdated",
        payload: { section_key: s.key, content: draft.value[s.key] },
      });
    }
  }
  if (!changed.length) return;
  saving.value = true;
  saveError.value = null;
  try {
    await pushEvents(changed);
    await queryClient.invalidateQueries({ queryKey: ["weekly-report", id] });
  } catch (e) {
    saveError.value = e instanceof Error ? e.message : String(e);
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <PageLayout>
    <RouterLink
      :to="{ name: 'weekly-reports', params: { projectId: $route.params.projectId } }"
      class="text-sm text-neutral-500 hover:text-neutral-900"
    >
      ← Weekly reports
    </RouterLink>

    <div
      v-if="isPending"
      class="mt-4 text-neutral-500"
    >
      Loading…
    </div>
    <div
      v-else-if="isError"
      class="mt-4 rounded-md bg-semantic-danger/10 p-4 text-semantic-danger"
    >
      Failed to load: {{ error?.message }}
    </div>
    <div
      v-else-if="data"
      class="mt-4 grid gap-6"
    >
      <header class="flex items-start justify-between gap-4">
        <div>
          <h1 class="text-2xl font-extrabold text-neutral-900">
            {{ data.project_name }} — {{ data.week_label }}
          </h1>
          <p class="mt-1 text-neutral-500">
            {{ data.period_start }} to {{ data.period_end }}
            <span v-if="data.prepared_by">
              · Prepared by {{ data.prepared_by }}</span>
          </p>
        </div>
        <div class="flex shrink-0 flex-col items-end gap-2">
          <span
            class="rounded-full px-3 py-1 text-xs font-medium capitalize"
            :class="data.status === 'approved'
              ? 'bg-semantic-success/10 text-semantic-success'
              : data.status === 'submitted'
                ? 'bg-semantic-warning/10 text-semantic-warning'
                : 'bg-neutral-100 text-neutral-500'"
          >{{ data.status }}</span>
          <div class="flex gap-2">
            <button
              type="button"
              class="rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50"
              :disabled="pdfBusy"
              @click="downloadPdf"
            >
              {{ pdfBusy ? "…" : "PDF" }}
            </button>
            <button
              v-if="data.status === 'draft'"
              type="button"
              class="rounded-md border px-3 py-1.5 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50"
              :class="reviewFindings && reviewFindings.length
                ? 'border-semantic-warning text-semantic-warning'
                : 'border-neutral-300'"
              :disabled="busy || reviewBusy"
              @click="submit"
            >
              {{ reviewBusy
                ? "Checking…"
                : (reviewFindings && reviewFindings.length ? "Submit anyway" : "Submit") }}
            </button>
            <button
              v-if="data.status === 'submitted' && canApprove"
              type="button"
              class="rounded-md bg-brand-primary px-4 py-1.5 text-sm font-semibold text-brand-primary-on disabled:opacity-50"
              :disabled="busy"
              @click="approve"
            >
              Approve
            </button>
            <button
              v-if="data.status !== 'draft' && canApprove"
              type="button"
              class="rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50"
              :disabled="busy"
              @click="reopen"
            >
              Reopen
            </button>
          </div>
        </div>
      </header>

      <!-- Pre-submit review findings (advisory) -->
      <section
        v-if="reviewFindings && reviewFindings.length"
        class="rounded-xl border border-semantic-warning/40 bg-semantic-warning/5 p-4"
      >
        <p class="mb-2 text-sm font-medium text-neutral-800">
          {{ reviewMsg }} You can still submit.
        </p>
        <ul class="grid gap-1.5">
          <li
            v-for="(f, i) in reviewFindings"
            :key="i"
            class="flex items-start gap-2 text-sm"
          >
            <span class="shrink-0">{{ f.severity === "warning" ? "⚠️" : "💡" }}</span>
            <span>{{ f.message }}</span>
          </li>
        </ul>
      </section>

      <!-- RAG dashboard -->
      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <div class="mb-1 flex items-start justify-between gap-4">
          <h2 class="font-semibold">
            Project status
          </h2>
          <button
            v-if="data.status === 'draft'"
            type="button"
            class="shrink-0 rounded-md border border-brand-primary/40 bg-brand-primary/5 px-3 py-1.5 text-sm font-medium text-brand-primary hover:bg-brand-primary/10 disabled:opacity-50"
            :disabled="ragBusy"
            title="Suggest each rating from this week's data"
            @click="suggestRag"
          >
            {{ ragBusy ? "Suggesting…" : "✨ Suggest RAG" }}
          </button>
        </div>
        <p class="mb-4 text-xs text-neutral-400">
          The 30-second RAG view for stakeholders.
        </p>
        <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div
            v-for="dim in RAG_DIMS"
            :key="dim.key"
            class="rounded-lg border border-neutral-100 p-3"
          >
            <p class="mb-2 text-sm font-medium text-neutral-700">
              {{ dim.label }}
            </p>
            <div class="flex gap-1">
              <button
                v-for="opt in RAG_OPTS"
                :key="opt.value"
                type="button"
                class="h-8 w-8 rounded-full text-xs font-bold transition"
                :class="data.rag[dim.key] === opt.value
                  ? opt.cls
                  : 'bg-neutral-100 text-neutral-400 hover:bg-neutral-200'"
                :disabled="busy"
                @click="setRag(dim.key, opt.value)"
              >
                {{ opt.label }}
              </button>
            </div>
            <!-- AI suggestion (advisory) — click to apply. -->
            <p
              v-if="ragSuggestions && ragSuggestions[dim.key]"
              class="mt-2 text-xs text-neutral-500"
            >
              ✨ Suggested:
              <button
                type="button"
                class="font-semibold underline decoration-dotted hover:text-neutral-900 disabled:opacity-50"
                :disabled="busy"
                @click="setRag(dim.key, ragSuggestions[dim.key].status)"
              >
                {{ RAG_WORD[ragSuggestions[dim.key].status] }}
              </button>
              — {{ ragSuggestions[dim.key].rationale }}
            </p>
          </div>
        </div>
      </section>

      <!-- Generated rollup -->
      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <h2 class="mb-4 font-semibold">
          This week — generated from the diaries
        </h2>
        <div class="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div>
            <p class="text-2xl font-bold">
              {{ data.rollup.shift_count }}
            </p>
            <p class="text-sm text-neutral-500">
              Shifts
            </p>
          </div>
          <div>
            <p class="text-2xl font-bold">
              {{ data.rollup.briefings }}
            </p>
            <p class="text-sm text-neutral-500">
              Briefings / toolbox talks
            </p>
          </div>
          <div>
            <p class="text-2xl font-bold">
              {{ data.rollup.checks_completed }}
            </p>
            <p class="text-sm text-neutral-500">
              ITP checks completed
            </p>
          </div>
          <div>
            <p class="text-2xl font-bold">
              {{ data.rollup.photos }}
            </p>
            <p class="text-sm text-neutral-500">
              Photos on file
            </p>
          </div>
          <div>
            <p class="text-2xl font-bold">
              {{ data.rollup.defects_open }}/{{ data.rollup.defects_total }}
            </p>
            <p class="text-sm text-neutral-500">
              Defects open / total
            </p>
          </div>
          <div>
            <p class="text-2xl font-bold">
              {{ data.rollup.close_calls }}
            </p>
            <p class="text-sm text-neutral-500">
              Close calls
            </p>
          </div>
          <div>
            <p class="text-2xl font-bold">
              {{ data.rollup.good_practice }}
            </p>
            <p class="text-sm text-neutral-500">
              Good practice
            </p>
          </div>
        </div>

        <h3 class="mb-2 mt-6 text-sm font-semibold text-neutral-700">
          Works completed
        </h3>
        <ul
          v-if="data.rollup.works_completed.length"
          class="grid list-disc gap-1 pl-5 text-sm"
        >
          <li
            v-for="(w, i) in data.rollup.works_completed"
            :key="i"
          >
            {{ w }}
          </li>
        </ul>
        <p
          v-else
          class="text-sm text-neutral-400"
        >
          No diary entries logged this week.
        </p>

        <h3 class="mb-2 mt-6 text-sm font-semibold text-neutral-700">
          Shifts
        </h3>
        <ul
          v-if="data.rollup.diaries.length"
          class="grid gap-1 text-sm"
        >
          <li
            v-for="d in data.rollup.diaries"
            :key="d.id"
          >
            <RouterLink
              :to="{ name: 'site-diary-detail', params: { projectId: $route.params.projectId, id: d.id } }"
              class="text-brand-primary hover:underline"
            >
              {{ d.diary_date }}
            </RouterLink>
            <span class="text-neutral-400"> · {{ d.status }}</span>
          </li>
        </ul>
        <p
          v-else
          class="text-sm text-neutral-400"
        >
          No shifts in this period.
        </p>
      </section>

      <!-- Editable narrative -->
      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <div class="mb-4 flex items-start justify-between gap-4">
          <h2 class="font-semibold">
            Narrative
          </h2>
          <button
            v-if="data.status === 'draft'"
            type="button"
            class="shrink-0 rounded-md border border-brand-primary/40 bg-brand-primary/5 px-3 py-1.5 text-sm font-medium text-brand-primary hover:bg-brand-primary/10 disabled:opacity-50"
            :disabled="drafting"
            title="Draft the empty sections from this week's rolled-up data"
            @click="draftNarrative"
          >
            {{ drafting ? "Drafting…" : "✨ Draft narrative" }}
          </button>
        </div>
        <p
          v-if="draftMsg"
          class="mb-3 text-sm text-neutral-500"
        >
          {{ draftMsg }}
        </p>
        <div class="grid gap-4">
          <label
            v-for="s in SECTIONS"
            :key="s.key"
            class="grid gap-1 text-sm"
          >
            <span class="text-neutral-500">{{ s.label }}</span>
            <textarea
              v-model="draft[s.key]"
              rows="3"
              class="rounded-md border border-neutral-300 px-3 py-2"
            />
          </label>
        </div>
        <p
          v-if="saveError"
          class="mt-3 text-sm text-semantic-danger"
        >
          {{ saveError }}
        </p>
        <button
          type="button"
          class="mt-4 rounded-md bg-brand-primary px-4 py-2 font-semibold text-brand-primary-on transition hover:opacity-90 disabled:opacity-50"
          :disabled="saving"
          @click="saveNarrative"
        >
          {{ saving ? "Saving…" : "Save narrative" }}
        </button>
      </section>

      <!-- Record + history -->
      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <h2 class="mb-3 font-semibold">
          Record
        </h2>
        <dl class="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
          <template v-if="data.created_by_name">
            <dt class="text-neutral-500">
              Created by
            </dt>
            <dd>{{ data.created_by_name }} · {{ fmt(data.created_at) }}</dd>
          </template>
          <template v-if="data.submitted_at">
            <dt class="text-neutral-500">
              Submitted by
            </dt>
            <dd>{{ data.submitted_by_name ?? "—" }} · {{ fmt(data.submitted_at) }}</dd>
          </template>
          <template v-if="data.approved_at">
            <dt class="text-neutral-500">
              Approved by
            </dt>
            <dd>{{ data.approved_by_name ?? "—" }} · {{ fmt(data.approved_at) }}</dd>
          </template>
          <dt class="text-neutral-500">
            Last updated
          </dt>
          <dd>{{ fmt(data.last_updated_at) }}<span v-if="data.last_updated_by_name"> · {{ data.last_updated_by_name }}</span></dd>
        </dl>

        <h3 class="mb-2 mt-5 text-xs font-semibold uppercase tracking-wide text-neutral-400">
          History
        </h3>
        <ol class="grid gap-1 text-sm">
          <li
            v-for="h in history ?? []"
            :key="h.seq"
            class="flex flex-wrap gap-x-2 text-neutral-600"
          >
            <span class="font-medium text-neutral-800">{{ h.label }}</span>
            <span class="text-neutral-400">·</span>
            <span>{{ h.actor_name ?? "—" }}</span>
            <span class="text-neutral-400">·</span>
            <span class="text-neutral-400">{{ fmt(h.at) }}</span>
          </li>
        </ol>
      </section>
    </div>
  </PageLayout>
</template>
